import tokenIcon from '@/assets/images/ic_token_grey.svg'
import { Tip } from '@/shared/components/ui/tooltip'
import { IS_SERVER_TEST, useCanvasUrls } from '@/shared/config/global-config-store'
import { modLabel } from '@/shared/lib/utils/platform-utils'
import { Info, Loader2, Send } from 'lucide-react'
import { type MouseEventHandler, useEffect, useRef } from 'react'

export type SubmitBtnProps = {
	credit: number | string | null
	noCredit?: boolean,
	creditRule?: React.ReactNode,
	onClick: MouseEventHandler<HTMLButtonElement>
	disabled?: boolean
	loading?: boolean
	ariaLabel?: string
	strikethroughTip?: string
}

function isEditableElement(target: Element | null) {
	if (!(target instanceof HTMLElement)) {
		return false
	}

	if (target.isContentEditable || target.closest('[contenteditable="true"]')) {
		return true
	}

	return ['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName)
}

export function SubmitBtn({
	credit,
	noCredit,
	creditRule,
	onClick,
	disabled = false,
	loading = false,
	ariaLabel = '提交任务',
	strikethroughTip,
}: SubmitBtnProps) {
	const buttonRef = useRef<HTMLButtonElement>(null)
	const propsRef = useRef({ onClick, disabled, loading })
	const { pointRuleUrl, managerDetailUrl } = useCanvasUrls()

	useEffect(() => {
		propsRef.current = { onClick, disabled, loading }
	})

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			const { disabled, loading, onClick } = propsRef.current
			if (disabled || loading) return

			const isMac = navigator.userAgent.toLowerCase().includes('mac')
			const isCmdOrCtrl = isMac ? e.metaKey : e.ctrlKey

			if (isCmdOrCtrl && e.key === 'Enter') {
				if (!buttonRef.current) return

				const nodeEl = buttonRef.current.closest('.react-flow__node')
				const toolbarEl = buttonRef.current.closest('.react-flow__node-toolbar')
				const activeElement = document.activeElement

				if (nodeEl || toolbarEl) {
					const containerEl = nodeEl || toolbarEl
					const isFocusedInside = containerEl?.contains(activeElement)

					if (isFocusedInside && isEditableElement(activeElement)) {
						e.preventDefault()
						e.stopPropagation()
						onClick(e as any)
					}
				}
			}
		}

		window.addEventListener('keydown', handleKeyDown, { capture: true })
		return () => window.removeEventListener('keydown', handleKeyDown, { capture: true })
	}, [])

	const baseCreditContent = !noCredit ? (
		<div className={`inline-flex items-center gap-1 text-[15px] text-[#A4A4A4] ${strikethroughTip ? 'cursor-help' : ''}`}>
			<img src={tokenIcon} alt="" className="h-3" aria-hidden="true" draggable={false} />
			<span>{strikethroughTip ? '免费' : credit}</span>
		</div>
	) : null

	const creditContent = strikethroughTip && baseCreditContent ? (
		<Tip content={strikethroughTip} delayDuration={0}>
			{baseCreditContent}
		</Tip>
	) : baseCreditContent

	const tipMsg = disabled ? `${ariaLabel}` : `${ariaLabel} (${modLabel()}+Enter)`;
	const submitButton = (
		<Tip content={`${tipMsg}`} delayDuration={0}>
			<button
				ref={buttonRef}
				type="button"
				className="nodrag inline-flex h-7 w-7 items-center justify-center rounded-[8px] bg-[#4096F3] text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
				onClick={onClick}
				disabled={disabled || loading}
				aria-label={ariaLabel}
			>
				{loading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					<Send className="h-4 w-4 fill-current" />
				)}
			</button>
		</Tip>
	)

	return (
		<div className="flex items-center gap-2">
			{creditContent}
			{creditRule && IS_SERVER_TEST && (
				<Tip
					style={{ maxWidth: 880 }}
					content={
						<div className="flex flex-col gap-2">
							<div className="whitespace-pre-wrap leading-relaxed">{creditRule}</div>
							<div className="text-[13px] text-[#A4A4A4] pt-2 mt-1 border-t border-[#5D5D5D]/50">
								点击查看{' '}
								<a
									href={pointRuleUrl || '#'}
									target="_blank"
									rel="noopener noreferrer"
									onClick={(e) => {
										if (!pointRuleUrl) {
											e.preventDefault();
											console.log('preview 算力消耗规则 - no url')
										}
									}}
									className="text-[#4096F3] text-[13px] underline underline-offset-2 transition-opacity hover:opacity-80"
								>
									算力消耗规则
								</a>
								{' '}
								<a
									href={managerDetailUrl || '#'}
									target="_blank"
									rel="noopener noreferrer"
									onClick={(e) => {
										if (!managerDetailUrl) {
											e.preventDefault();
											console.log('preview 算力明细 - no url')
										}
									}}
									className="text-[#4096F3] text-[13px] underline underline-offset-2 transition-opacity hover:opacity-80 ml-1"
								>
									算力明细
								</a>
							</div>
						</div>
					}
					interactive={true}
					delayDuration={0}
					className="max-w-xs bg-[#3D3D3D] text-[#E0E0E0] text-[13px] p-3 shadow-[0_4px_10px_0_rgba(0,0,0,0.5)] border border-[#5D5D5D]"
					arrowClassName="bg-[#3D3D3D] fill-[#3D3D3D]"
				>
					<Info className="nodrag mr-1 h-4 w-4 cursor-help text-[#A4A4A4] transition-colors hover:text-[#4096F3] focus:outline-none" />
				</Tip>
			)}
			{submitButton}
		</div>
	)
}
